/**
 * Constants and configuration for the new authentication flow
 *
 * This module defines all constants used across the new auth flow including:
 * - Cookie names and values
 * - Profile status values
 * - WWW-Authenticate header values
 * - Auth scenarios that UFE can detect
 */

/**
 * Cookie name for the readable profileStatus cookie that acts as an access token indicator
 * Set by backend with a TTL of 60 minutes
 * If this cookie exists, it indicates tokens are fresh (< 60 mins old)
 * Value will be one of: '0', '2', '3', '4' (matching PROFILE_STATUS)
 */
export const PROFILE_STATUS_COOKIE_NAME = 'sis';

/**
 * Cookie name for the readable isSSI cookie
 * Set by backend with variable TTL:
 * - 60 mins for anonymous users and scenario 6
 * - 150 days for scenarios 2-5
 * Value will be: 'true' or 'false'
 */
export const IS_SSI_COOKIE_NAME = 'sks';

/**
 * Profile status values
 * Matches the existing USER_PROFILE_STATUS from utils/User.js
 */
export const PROFILE_STATUS = {
    ANONYMOUS: 0, // Anonymous user, no login
    RECOGNIZED: 2, // Recognized user (logged in)
    RECOGNIZED_SSI: 3, // Recognized via SSI
    LOGGED_IN: 4 // Fully logged in user
};

/**
 * WWW-Authenticate header values to check for
 */
export const WWW_AUTHENTICATE = {
    HEADER_NAME: 'www-authenticate',
    TOKEN_EXCHANGE_ERROR: 'error="Token exchange"'
};

/**
 * Scenarios that UFE can detect
 * Note: UFE cannot detect Scenario 2 (login), 3 (proactive refresh), or 6 (150 days)
 * Those are handled by backend or external flows
 */
export const AUTH_SCENARIOS = {
    ANONYMOUS_FLOW: 'ANONYMOUS_FLOW', // Scenario 1
    WITHIN_ONE_HOUR: 'WITHIN_ONE_HOUR', // Scenario 4
    AFTER_ONE_HOUR_BEFORE_150_DAYS: 'AFTER_ONE_HOUR_BEFORE_150_DAYS', // Scenario 5
    LOG_USER_OUT_AFTER_150_DAYS: 'LOG_USER_OUT_AFTER_150_DAYS' // Scenario 6
};

/**
 * Error messages used by the new auth flow.
 * Some values may be matched against backend API responses, while others are
 * frontend fallback messages shown when no specific backend message is available.
 * Do not treat every value in this object as a guaranteed backend API contract.
 */
export const API_ERROR_MESSAGES = {
    SIGN_IN_REQUIRED: 'A technical error has occurred. Please sign in again.',
    GENERIC_ERROR: 'A technical error has occurred. Please try again or contact support if the problem persists.' // Frontend fallback message, not a backend contract
};

/**
 * Feature flag to enable/disable new auth flow
 * Should be controlled via Sephora.configurationSettings
 * @returns {boolean} True if new auth flow is enabled, false otherwise
 */
export const isNewAuthFlowEnabled = () => {
    return Boolean(Sephora.configurationSettings?.cookieBasedAuth?.isEnabled === true);
};
